import React, { useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ComposedChart, Bar } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus, ArrowUpRight } from "lucide-react";

export const COLOURS = ["#4f46e5", "#0891b2", "#059669", "#d97706", "#e11d48", "#7c3aed", "#0d9488", "#ea580c", "#2563eb", "#be123c"];
const fmtMonth = (m) => new Date(`${m}-01`).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });

export function InterestChart({ monthly, keywords }) {
  return (
    <div className="h-80" data-testid="trends-interest-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={monthly} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid stroke="#f1f5f9" />
          <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11 }} minTickGap={24} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
          <Tooltip labelFormatter={fmtMonth} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {keywords.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={COLOURS[i % 10]} strokeWidth={2} dot={false} />)}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function SalesVsTrend({ monthly, keywords, categories, sales }) {
  const [kw, setKw] = useState(keywords[0]);
  const cat = categories[kw];
  const rows = useMemo(() => monthly.slice(-36).map((m) => ({ month: m.month, interest: m[kw], units: cat ? (sales[m.month] || {})[cat] || 0 : 0 })), [monthly, kw, cat, sales]);
  const total = rows.reduce((s, r) => s + r.units, 0);
  return (
    <div data-testid="trends-sales-card">
      <div className="flex flex-wrap gap-2 mb-3">
        {keywords.map((k) => (
          <button key={k} onClick={() => setKw(k)} data-testid={`trends-sales-kw-${k}`}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${k === kw ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{k}</button>
        ))}
      </div>
      <p className="text-xs text-slate-500 mb-2" data-testid="trends-sales-note">
        {cat ? `Bars: ${cat} units sold on TedOps (sale jobs, accepted quotes, staff entries) · ${total} in the last 3 years. Line: Google search interest.` : `"${kw}" isn't one of the 5 product categories, so only search interest is shown.`}
      </p>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="#f1f5f9" />
            <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11 }} minTickGap={20} />
            <YAxis yAxisId="i" domain={[0, 100]} tick={{ fontSize: 11 }} />
            <YAxis yAxisId="u" orientation="right" allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip labelFormatter={fmtMonth} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="u" dataKey="units" name="Units sold" fill="#c7d2fe" radius={[4, 4, 0, 0]} />
            <Line yAxisId="i" type="monotone" dataKey="interest" name="Search interest" stroke="#4f46e5" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const List = ({ items, rising, kw }) => !items?.length ? <p className="text-xs text-slate-400">Not enough search volume.</p> : (
  <ul className="space-y-1">
    {items.map((q) => (
      <li key={q.query} className="flex items-center gap-2 text-sm" data-testid={`trends-${rising ? "rising" : "top"}-${kw}-${q.query}`}>
        <a href={`https://trends.google.com/trends/explore?geo=GB&q=${encodeURIComponent(q.query)}`} target="_blank" rel="noopener noreferrer" className="text-slate-700 hover:text-indigo-700 truncate">{q.query}</a>
        <span className={`ml-auto shrink-0 text-xs font-semibold ${rising ? "text-emerald-600" : "text-slate-400"}`}>{rising ? (q.value >= 5000 ? "Breakout" : `+${q.value}%`) : q.value}</span>
      </li>
    ))}
  </ul>
);

export function RelatedSearches({ keywords, rising, top }) {
  const [kw, setKw] = useState(keywords[0]);
  return (
    <div data-testid="trends-related-card">
      <div className="flex flex-wrap gap-2 mb-4">
        {keywords.map((k) => (
          <button key={k} onClick={() => setKw(k)} data-testid={`trends-related-kw-${k}`}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${k === kw ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{k}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div><h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" />Rising: campaign ideas</h3><List items={rising[kw]} rising kw={kw} /></div>
        <div><h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Top searches</h3><List items={top[kw]} kw={kw} /></div>
      </div>
    </div>
  );
}

export function KeywordEditor({ keywords, onSave, saving }) {
  const [list, setList] = useState(keywords);
  const [v, setV] = useState("");
  const add = () => { const t = v.trim(); if (t && !list.includes(t) && list.length < 10) setList([...list, t]); setV(""); };
  return (
    <div className="space-y-3" data-testid="trends-keyword-editor">
      <div className="flex flex-wrap gap-2">
        {list.map((k) => (
          <span key={k} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-slate-100 text-xs text-slate-700">{k}
            <button onClick={() => setList(list.filter((x) => x !== k))} className="p-0.5 rounded-full hover:bg-slate-200" data-testid={`trends-kw-remove-${k}`}><X className="w-3 h-3" /></button></span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a keyword (max 10)" className="h-9" data-testid="trends-kw-input" />
        <Button variant="outline" size="sm" className="h-9 gap-1" onClick={add} disabled={list.length >= 10} data-testid="trends-kw-add-btn"><Plus className="w-4 h-4" />Add</Button>
        <Button size="sm" className="h-9" onClick={() => onSave(list)} disabled={saving || !list.length} data-testid="trends-kw-save-btn">Save</Button>
      </div>
      <p className="text-[11px] text-slate-400">Search interest is 0–100 relative to the busiest week. Keywords are compared in groups of 5, so lines are only directly comparable within the same group.</p>
    </div>
  );
}
